import { ArrowRight, Menu, X, Zap, type LucideIcon } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { cn } from "@hostdock/ui";

export interface ShellNavItem {
  label: string;
  to: string;
  icon: LucideIcon;
}

const isActive = (path: string, to: string) => (to === "/" ? path === "/" : path === to || path.startsWith(`${to}/`));

/** HostDock mark on a teal tile, as in the Panel sidebar. */
export function BrandMark({ size = 28 }: { size?: number }) {
  return (
    <span className="flex shrink-0 items-center justify-center rounded-[7px] bg-[linear-gradient(140deg,#25c6d1,#0a9fb1)] shadow-[0_2px_8px_rgb(10_160_180/0.35)]" style={{ width: size, height: size }} aria-hidden>
      <svg width={size * 0.64} height={size * 0.64} viewBox="0 0 24 24">
        <rect x="5" y="4.5" width="3" height="9.5" rx="1" fill="#fff" />
        <rect x="10.5" y="4.5" width="3" height="9.5" rx="1" fill="#fff" opacity="0.9" />
        <rect x="16" y="4.5" width="3" height="9.5" rx="1" fill="#fff" opacity="0.8" />
        <rect x="4" y="16.5" width="16" height="2.6" rx="1.3" fill="#fff" />
      </svg>
    </span>
  );
}

/**
 * The customer Panel frame: a fixed navy sidebar (groups separated by rules, a documentation card at the bottom) and a
 * compact white top bar. Below the lg breakpoint the sidebar becomes a drawer behind a menu button.
 */
export function PanelShell({ nav, currentPath, docsHref, topbar, children }: {
  nav: ShellNavItem[][];
  currentPath: string;
  /** Where "View documentation" goes; an internal path or an external URL. */
  docsHref: string;
  topbar: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [currentPath]);
  const external = /^https?:\/\//.test(docsHref);

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex h-[57px] shrink-0 items-center gap-[8px] px-[16px]">
        <BrandMark size={30} />
        <span className="text-[19px] leading-none font-medium tracking-[-0.01em] text-white">HostDock</span>
      </div>
      <nav className="hd-scroll flex-1 overflow-y-auto px-[13px] pt-[23px]" aria-label="Main">
        {nav.map((group, gi) => (
          <div key={gi}>
            {gi > 0 && <div className="mx-[5px] my-[14px] h-px bg-nav-line" aria-hidden />}
            <ul className="flex flex-col gap-1">
              {group.map((item) => {
                const active = isActive(currentPath, item.to);
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "relative flex h-9 items-center gap-[16px] overflow-hidden rounded-lg px-[12px] text-[12px] text-nav-fg transition-colors",
                        active ? "bg-[linear-gradient(90deg,#1b4457,#163246)] font-medium text-white shadow-[inset_0_0_0_1px_rgb(255_255_255/0.04)]" : "hover:bg-nav-hover hover:text-white",
                      )}
                    >
                      {active && <span className="absolute inset-y-0 left-0 w-[3px] bg-[#1fc3d2]" aria-hidden />}
                      <Icon size={20} strokeWidth={active ? 2 : 1.6} className={active ? "text-white drop-shadow-[0_0_6px_rgb(80_220_235/0.7)]" : "text-nav-icon"} />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
      <div className="shrink-0 px-[13px] pt-4 pb-[39px] [@media(max-height:780px)]:hidden">
        <div className="rounded-xl border border-nav-card-line bg-nav-card p-[14px] pb-[15px]">
          <div className="flex items-start gap-3">
            <span className="mt-[1px] flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg bg-[#0f3143] text-[#3fd6e2] shadow-[0_0_14px_rgb(63_214_226/0.35)]">
              <Zap size={16} fill="currentColor" />
            </span>
            <p className="text-[12px] leading-[17px] font-semibold text-white">Build faster<br />with HostDock</p>
          </div>
          <p className="mt-[9px] text-[12px] leading-[17px] text-[#b6c2cf]">Powerful, reliable and developer-friendly hosting.</p>
          {external ? (
            <a href={docsHref} target="_blank" rel="noreferrer" className="mt-[12px] flex h-[34px] items-center justify-center gap-2 rounded-lg bg-nav-button text-[12.5px] font-medium text-white transition-colors hover:brightness-125">
              View documentation <ArrowRight size={14} />
            </a>
          ) : (
            <Link to={docsHref} className="mt-[12px] flex h-[34px] items-center justify-center gap-2 rounded-lg bg-nav-button text-[12.5px] font-medium text-white transition-colors hover:brightness-125">
              View documentation <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-full">
      <aside className="hidden w-[205px] shrink-0 bg-sidebar lg:block">{sidebar}</aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-[#06101a]/55 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="hd-enter absolute inset-y-0 left-0 w-[240px] bg-sidebar shadow-lg">
            <button type="button" onClick={() => setOpen(false)} className="absolute top-4 right-3 rounded-lg p-1.5 text-nav-fg hover:bg-nav-hover" aria-label="Close menu">
              <X size={16} />
            </button>
            {sidebar}
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-[57px] shrink-0 items-center gap-2 border-b border-line/70 bg-surface/95 px-3 backdrop-blur-xl sm:px-[20px] lg:pr-[17px]">
          <button type="button" onClick={() => setOpen(true)} className="rounded-lg p-1.5 text-muted hover:bg-surface-2 lg:hidden" aria-label="Open menu">
            <Menu size={19} />
          </button>
          <div className="min-w-0 flex-1">{topbar}</div>
        </header>
        <main className="hd-scroll flex-1 overflow-x-hidden overflow-y-auto">
          <div key={currentPath} className="hd-enter mx-auto w-full max-w-[1640px] px-3 pt-[13px] pb-8 sm:px-[15px]">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
